// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ResizeHandle.tsx
 * @input direction, isReversed, hasDivider, isAlwaysVisible, pillPlacement, ResizableProps
 * @output Styled drag handle with WAI-ARIA separator role and keyboard support
 * @position Between resizable panels; consumed directly by builders
 *
 * The handle element is 1px wide (the divider line itself), with an
 * absolutely-positioned wider hit area for pointer interaction.
 * Pill grip indicator can sit on either side of the divider (or centered)
 * via pillPlacement. Default 'auto' places the pill on the panel side and
 * flips when the panel collapses to 0px so it stays accessible.
 *
 * Pill placement uses a single stylex dynamic style that accepts a direction
 * multiplier (-1 or 1). The pill element has its own themeProps
 * ('resize-handle-pill') so themes can target size/shape directly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, durationVars, easeVars, radiusVars, spacingVars } from "../theme/tokens.stylex.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const KEYBOARD_STEP = 10;
const KEYBOARD_LARGE_STEP = 50;
function resolveEffectiveSide(pillPlacement, isReversed, isCollapsed) {
  if (pillPlacement !== 'auto') {
    return pillPlacement;
  }
  const panelSide = isReversed ? 'end' : 'start';
  if (isCollapsed) {
    return panelSide === 'start' ? 'end' : 'start';
  }
  return panelSide;
}

/**
 * Hit area bias percentage. When the pill is off-center, the grab zone
 * shifts ~2:1 toward the pill so users can reach the visible grip easily.
 */
function hitAreaBias(effectiveSide) {
  if (effectiveSide === 'center') {
    return '50%';
  }
  return effectiveSide === 'start' ? '66.67%' : '33.33%';
}
const styles = {
  handle: {
    kVAEAm: "x1n2onr6",
    kmuXW: "x2lah0s",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kWkggS: "x1m4xfpy",
    k1ekBW: "x15406qy",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    kI3sdo: "x1a2a7pz x17nn4n9",
    kInvED: "xh2nty3",
    $$css: true
  },
  overlay: {
    kVAEAm: "x10l6tqk",
    kY2c9j: "xhtitgo",
    kWkggS: "xjbqb8w",
    $$css: true
  },
  overlayHorizontal: {
    kt4wiu: "xtijo5x",
    k87sOh: "x13vifvy",
    krVfgx: "x1ey2m1c",
    kzqmXN: "xkhob8h",
    $$css: true
  },
  overlayVertical: {
    krVfgx: "x1ey2m1c",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    kZKoxP: "x5kfih4",
    $$css: true
  },
  horizontal: {
    kzqmXN: "x1i1rx1s",
    kZKoxP: "x5yr21d",
    kkrTdU: "xicojor",
    $$css: true
  },
  vertical: {
    kZKoxP: "xjm9jq1",
    kzqmXN: "xh8yej3",
    kkrTdU: "xyozgr7",
    $$css: true
  },
  noDividerHorizontal: {
    kWkggS: "xjbqb8w",
    kzqmXN: "xnalus7",
    $$css: true
  },
  noDividerVertical: {
    kWkggS: "xjbqb8w",
    kZKoxP: "xqtp20y",
    $$css: true
  },
  handleHover: {
    kWkggS: "x1m4xfpy",
    $$css: true
  },
  handleActive: {
    kWkggS: "x7njt3n",
    $$css: true
  },
  disabled: {
    kkrTdU: "xt0e3qv",
    kfzvcC: "x47corl",
    $$css: true
  },
  hitArea: {
    kVAEAm: "x10l6tqk",
    kY2c9j: "x1vjfegm",
    kFalU9: "x5ve5x3",
    kfSwDN: "x87ps6o",
    $$css: true
  },
  hitAreaHorizontal: {
    kzqmXN: "x12xnipv",
    k87sOh: "x13vifvy",
    krVfgx: "x1ey2m1c",
    kbCHJM: "x1nrll8i",
    kkrTdU: "xicojor",
    $$css: true
  },
  hitAreaVertical: {
    kZKoxP: "x6b6gus",
    kbCHJM: "xu96u03",
    kCIrl2: "x3m8u43",
    k87sOh: "xwa60dl",
    kkrTdU: "xyozgr7",
    $$css: true
  },
  pill: {
    kVAEAm: "x10l6tqk",
    kY2c9j: "xhtitgo",
    kfzvcC: "x47corl",
    kaIpWk: "xjspbzw",
    kWkggS: "x1m4xfpy",
    k1ekBW: "x1wbls7o",
    kIyJzY: "xuedmi6",
    kAMwcw: "xlr8y92",
    k87sOh: "xwa60dl",
    kbCHJM: "x1nrll8i",
    k3aq6I: "x11lhmoz",
    $$css: true
  },
  pillHorizontal: {
    kzqmXN: "x1g8rjiy",
    kZKoxP: "x18to7ep",
    $$css: true
  },
  pillVertical: {
    kzqmXN: "xgc8j7m",
    kZKoxP: "xuoj239",
    $$css: true
  },
  pillHidden: {
    kSiTet: "xg01cxk",
    $$css: true
  },
  pillVisible: {
    kSiTet: "x1hc1fzr",
    $$css: true
  },
  pillHover: {
    kSiTet: "x1hc1fzr",
    kWkggS: "x1m4xfpy",
    $$css: true
  },
  pillActive: {
    kSiTet: "x1hc1fzr",
    kWkggS: "x7njt3n",
    $$css: true
  }
};

// Dynamic styles — avoids inline style overrides.
// Each axis gets its own function since StyleX requires static structure.
const _temp = {
  k3aq6I: "xsqj5wx",
  "$$css": true
};
const _temp2 = {
  k3aq6I: "xsqj5wx",
  "$$css": true
};
const _temp3 = {
  kbCHJM: "xu96u03",
  k3aq6I: "xsqj5wx",
  "$$css": true
};
const _temp4 = {
  k87sOh: "x13vifvy",
  k3aq6I: "xsqj5wx",
  "$$css": true
};
const dynamicStyles = {
  hitAreaBiasX: pct => [_temp, {
    "--x-transform": `translateX(-${pct})` != null ? `translateX(-${pct})` : undefined
  }],
  hitAreaBiasY: pct => [_temp2, {
    "--x-transform": `translateY(-${pct})` != null ? `translateY(-${pct})` : undefined
  }],
  pillOffsetX: dir => [_temp3, {
    "--x-transform": `translate(calc(${dir} * (100% + ${spacingVars['--spacing-1']})), -50%)` != null ? `translate(calc(${dir} * (100% + ${spacingVars['--spacing-1']})), -50%)` : undefined
  }],
  pillOffsetY: dir => [_temp4, {
    "--x-transform": `translate(-50%, calc(${dir} * (100% + ${spacingVars['--spacing-1']})))` != null ? `translate(-50%, calc(${dir} * (100% + ${spacingVars['--spacing-1']})))` : undefined
  }]
};
/**
 * Draggable resize handle placed between resizable panels. Renders as a thin
 * divider line with a wider invisible hit area and optional pill grip indicator.
 * Supports keyboard resizing via arrow keys and WAI-ARIA separator role.
 *
 * The pill element uses class `astryx-resize-handle-pill` for theme targeting.
 *
 * @example
 * ```
 * <ResizeHandle
 *   resizable={sidebar.props}
 *   direction="horizontal"
 *   hasDivider />
 * ```
 */
export function ResizeHandle({
  direction = 'horizontal',
  position: positionMode = 'inline',
  isReversed = false,
  isDisabled = false,
  hasDivider = false,
  isAlwaysVisible = true,
  pillPlacement = 'auto',
  label: labelFromProps,
  resizable,
  children,
  xstyle,
  className,
  ref,
  ...props
}) {
  const t = useTranslator();
  const label = labelFromProps ?? t('@astryx.resizable.handle.label');
  const handleRef = useRef(null);
  // Removes the in-flight drag's window listeners (and resets body styles).
  // Held in a ref so unmount can tear down a drag that never got a pointerup.
  const dragCleanupRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isHorizontal = direction === 'horizontal';
  const isOverlay = positionMode === 'overlay';
  const sign = isReversed ? -1 : 1;
  const effectiveSide = resolveEffectiveSide(pillPlacement, isReversed, resizable?._isCollapsed ?? false);
  const getRTLMultiplier = useCallback(() => {
    const el = handleRef.current;
    if (!el) {
      return 1;
    }
    return getComputedStyle(el).direction === 'rtl' ? -1 : 1;
  }, []);
  const isInteracting = isHovered || isFocused;

  // --- Pointer drag ---
  const handlePointerDown = useCallback(e => {
    if (isDisabled || !resizable) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    resizable._onResizeStart();
    const startPos = isHorizontal ? e.clientX : e.clientY;
    const rtl = isHorizontal ? getRTLMultiplier() : 1;
    document.body.style.cursor = isHorizontal ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    const onMove = ev => {
      const currentPos = isHorizontal ? ev.clientX : ev.clientY;
      const delta = (currentPos - startPos) * rtl * sign;
      resizable._onResizeMove(delta);
    };
    const onUp = () => {
      cleanup();
      setIsDragging(false);
      resizable._onResizeEnd();
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    const onCancel = () => {
      cleanup();
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    function cleanup() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      dragCleanupRef.current = null;
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    dragCleanupRef.current = cleanup;
  }, [isDisabled, resizable, isHorizontal, getRTLMultiplier, sign]);

  // --- Keyboard ---
  const handleKeyDown = useCallback(e => {
    if (isDisabled || !resizable) {
      return;
    }
    const step = e.shiftKey ? KEYBOARD_LARGE_STEP : KEYBOARD_STEP;
    const rtl = isHorizontal ? getRTLMultiplier() : 1;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        {
          e.preventDefault();
          resizable._onResizeStart();
          resizable._onResizeMove(step * (isHorizontal ? rtl : 1) * sign);
          resizable._onResizeEnd();
          break;
        }
      case 'ArrowLeft':
      case 'ArrowUp':
        {
          e.preventDefault();
          resizable._onResizeStart();
          resizable._onResizeMove(-step * (isHorizontal ? rtl : 1) * sign);
          resizable._onResizeEnd();
          break;
        }
      case 'Home':
        {
          e.preventDefault();
          resizable._onResizeStart();
          resizable._onResizeMove(resizable._minSizePx - resizable._size);
          resizable._onResizeEnd();
          break;
        }
      case 'End':
        {
          e.preventDefault();
          if (resizable._maxSizePx !== Infinity) {
            resizable._onResizeStart();
            resizable._onResizeMove(resizable._maxSizePx - resizable._size);
            resizable._onResizeEnd();
          }
          break;
        }
      case 'Enter':
        {
          e.preventDefault();
          if (resizable._collapsible) {
            resizable._onResizeStart();
            resizable._onResizeMove(resizable._isCollapsed ? resizable._minSizePx : -resizable._size);
            resizable._onResizeEnd();
          }
          break;
        }
    }
  }, [isDisabled, resizable, isHorizontal, getRTLMultiplier, sign]);

  // --- Double-click collapse ---
  const handleDoubleClick = useCallback(() => {
    if (isDisabled || !resizable || !resizable._collapsible) {
      return;
    }
    resizable._onResizeStart();
    resizable._onResizeMove(resizable._isCollapsed ? resizable._minSizePx : -resizable._size);
    resizable._onResizeEnd();
  }, [isDisabled, resizable]);

  // --- Cleanup on unmount ---
  // A drag in flight when the handle unmounts never gets its pointerup, so
  // tear down the window listeners here too — otherwise every pointermove
  // keeps driving the (still-mounted) region's resize state after the handle
  // is gone, and the body cursor/user-select overrides stick.
  useEffect(() => {
    return () => {
      if (dragCleanupRef.current) {
        dragCleanupRef.current();
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, []);

  // --- ARIA ---
  const ariaValueNow = resizable ? resizable._size : undefined;
  const ariaValueMin = resizable ? resizable._minSizePx : undefined;
  const ariaValueMax = resizable && resizable._maxSizePx !== Infinity ? resizable._maxSizePx : undefined;
  return /*#__PURE__*/_jsxs("div", {
    ref: mergeRefs(ref, handleRef),
    role: "separator",
    "aria-orientation": isHorizontal ? 'vertical' : 'horizontal',
    "aria-valuenow": ariaValueNow,
    "aria-valuemin": ariaValueMin,
    "aria-valuemax": ariaValueMax,
    "aria-label": label,
    "aria-disabled": isDisabled || undefined,
    tabIndex: isDisabled ? -1 : 0,
    onDoubleClick: handleDoubleClick,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    "data-resizing": isDragging || undefined,
    ...mergeProps(themeProps('resize-handle'), stylex.props(styles.handle, isOverlay && styles.overlay, isOverlay && (isHorizontal ? styles.overlayHorizontal : styles.overlayVertical), !isOverlay && (isHorizontal ? styles.horizontal : styles.vertical), !isOverlay && !hasDivider && (isHorizontal ? styles.noDividerHorizontal : styles.noDividerVertical), !isOverlay && hasDivider && isInteracting && !isDragging && styles.handleHover, !isOverlay && hasDivider && isDragging && styles.handleActive, isDisabled && styles.disabled, xstyle), className),
    ...props,
    // Keyboard resizing must fire from the focusable separator, not a child:
    // keydown fires on the focused element and bubbles up, so a handler on a
    // descendant never runs (per the WAI-ARIA window-splitter pattern).
    // Placed after {...props} and composed with any consumer onKeyDown so
    // this accessibility-critical handler can't be clobbered by a passed prop.
    onKeyDown: e => {
      props.onKeyDown?.(e);
      handleKeyDown(e);
    },
    children: [/*#__PURE__*/_jsx("div", {
      ...stylex.props(styles.hitArea, isHorizontal ? styles.hitAreaHorizontal : styles.hitAreaVertical, isHorizontal ? dynamicStyles.hitAreaBiasX(hitAreaBias(effectiveSide)) : dynamicStyles.hitAreaBiasY(hitAreaBias(effectiveSide)), isDisabled && styles.disabled),
      onPointerDown: handlePointerDown,
      onPointerEnter: () => setIsHovered(true),
      onPointerLeave: () => {
        if (!isDragging) {
          setIsHovered(false);
        }
      }
    }), children ?? /*#__PURE__*/_jsx("div", {
      ...mergeProps(themeProps('resize-handle-pill'), stylex.props(styles.pill, isHorizontal ? styles.pillHorizontal : styles.pillVertical, effectiveSide !== 'center' && (isHorizontal ? dynamicStyles.pillOffsetX(effectiveSide === 'start' ? -1 : 1) : dynamicStyles.pillOffsetY(effectiveSide === 'start' ? -1 : 1)), isAlwaysVisible ? styles.pillVisible : styles.pillHidden, isInteracting && !isDragging && styles.pillHover, isDragging && styles.pillActive))
    })]
  });
}
ResizeHandle.displayName = 'ResizeHandle';